import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Lock, ChevronRight, HelpCircle, Rocket } from 'lucide-react';
import Logo from '../common/Logo';

const Sidebar = ({
    isOpen,
    setIsOpen,
    navItems,
    activePath,
    tenantData,
    handleLogout,
    t,
    onProClick,
    onShowGuide
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [openSubmenus, setOpenSubmenus] = useState({});
    const [logoError, setLogoError] = useState(false);

    // Auto-expand submenu when navigating directly to a child route
    useEffect(() => {
        navItems.forEach(item => {
            if (item.children?.some(child => {
                if (!child.path) return false;
                if (child.exact) return location.pathname === child.path;
                return location.pathname.startsWith(child.path);
            })) {
                setOpenSubmenus(prev => ({ ...prev, [item.key || item.label]: true }));
            }
        });
    }, [location.pathname, navItems]);

    const toggleSubmenu = (key) => {
        setOpenSubmenus(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isSubmenuOpen = (item) => !!openSubmenus[item.key || item.label];

    const isParentActive = (item) =>
        item.children?.some(child => child.path && activePath(child));

    const handleParentClick = (item) => {
        const key = item.key || item.label;
        const isCurrentlyOpen = !!openSubmenus[key];

        if (!isOpen) {
            setIsOpen(true);
            setOpenSubmenus(prev => ({ ...prev, [key]: true }));
            // Auto-navigate to first child if opening
            if (item.children?.[0]?.path) {
                navigate(item.children[0].path);
            }
        } else {
            const willOpen = !isCurrentlyOpen;
            toggleSubmenu(key);
            // Auto-navigate to first child if opening
            if (willOpen && item.children?.[0]?.path) {
                navigate(item.children[0].path);
            }
        }
    };

    return (
        <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
            <div className="logo-container">
                {tenantData?.theme?.logoUrl && !logoError ? (
                    <div className="logo-icon" style={{ padding: '0', background: 'transparent', width: '40px', height: '40px', overflow: 'hidden' }}>
                        <img src={tenantData.theme.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={() => setLogoError(true)} />
                    </div>
                ) : (
                    <div className="logo-icon" style={{ background: 'transparent', padding: '0' }}>
                        <Logo size={40} />
                    </div>
                )}
                {isOpen && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                        {tenantData?.name ? (
                            <span className="logo-text" title={tenantData.name}>{tenantData.name}</span>
                        ) : (
                            <span style={{ fontSize: '1.5rem', fontWeight: 600, fontFamily: 'var(--font-logo)', color: 'var(--text-main)', position: 'relative', top: '2px' }}>ugra</span>
                        )}
                    </div>
                )}
            </div>

            <nav className="nav">
                {navItems.map((item, index) => {
                    const isFirstBottom = item.isBottom && !navItems[index - 1]?.isBottom;
                    const hasChildren = item.children?.length > 0;

                    // Parent item with children (accordion)
                    if (hasChildren) {
                        const submenuOpen = isSubmenuOpen(item);
                        const parentActive = isParentActive(item);
                        return (
                            <div key={item.key || item.label} style={isFirstBottom ? { marginTop: 'auto' } : {}}>
                                <button
                                    className={`nav-link ${parentActive && !submenuOpen ? 'active' : ''}`}
                                    style={{
                                        background: parentActive && !submenuOpen ? 'var(--primary)' : 'transparent',
                                        border: 'none',
                                        width: '100%',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleParentClick(item)}
                                >
                                    <item.icon size={20} />
                                    {isOpen && <span style={{ flex: 1 }}>{item.label}</span>}
                                    {isOpen && (
                                        <ChevronRight
                                            size={16}
                                            style={{
                                                transition: 'transform 0.25s',
                                                transform: submenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                </button>
                                {isOpen && submenuOpen && (
                                    <div style={{
                                        marginTop: '2px',
                                        marginLeft: '8px',
                                        paddingLeft: '8px',
                                        borderLeft: '2px solid var(--border)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '2px',
                                    }}>
                                        {item.children.map(child => {
                                            if (child.proOnly) {
                                                return (
                                                    <button
                                                        key={child.path}
                                                        className="nav-link"
                                                        style={{
                                                            opacity: 0.5,
                                                            cursor: 'pointer',
                                                            background: 'transparent',
                                                            border: 'none',
                                                            width: '100%',
                                                            textAlign: 'left',
                                                            fontSize: '0.875rem',
                                                            padding: '9px 12px',
                                                        }}
                                                        onClick={() => {
                                                            if (onProClick) onProClick();
                                                            if (window.innerWidth <= 1024) setIsOpen(false);
                                                        }}
                                                    >
                                                        <span style={{ flex: 1 }}>{child.label}</span>
                                                        <Lock size={12} style={{ opacity: 0.5 }} />
                                                    </button>
                                                );
                                            }
                                            return (
                                                <Link
                                                    key={child.path}
                                                    to={child.path}
                                                    className={`nav-link ${activePath(child) ? 'active' : ''}`}
                                                    style={{ fontSize: '0.875rem', padding: '9px 12px' }}
                                                    onClick={() => {
                                                        if (window.innerWidth <= 1024) setIsOpen(false);
                                                    }}
                                                >
                                                    <span>{child.label}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    }

                    // Pro-only item (no children)
                    if (item.proOnly) {
                        return (
                            <button
                                key={item.path}
                                className="nav-link"
                                style={{
                                    ...(isFirstBottom ? { marginTop: 'auto' } : {}),
                                    opacity: 0.5,
                                    cursor: 'pointer',
                                    background: 'transparent',
                                    border: 'none',
                                    width: '100%',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}
                                onClick={() => {
                                    if (onProClick) onProClick();
                                    if (window.innerWidth <= 1024) setIsOpen(false);
                                }}
                            >
                                <item.icon size={20} />
                                {isOpen && <span style={{ flex: 1 }}>{item.label}</span>}
                                {isOpen && <Lock size={14} style={{ opacity: 0.5 }} />}
                            </button>
                        );
                    }

                    // Regular item
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-link ${activePath(item) ? 'active' : ''}`}
                            style={isFirstBottom ? { marginTop: 'auto' } : {}}
                            onClick={() => {
                                if (window.innerWidth <= 1024) setIsOpen(false);
                            }}
                        >
                            <item.icon size={20} />
                            {isOpen && <span>{item.label}</span>}
                        </Link>
                    );
                })}
            </nav>

            {onShowGuide && (
                <button className="logout-btn" onClick={onShowGuide} style={{ marginTop: 'auto', color: 'var(--text-muted)', fontWeight: 400 }}>
                    <HelpCircle size={20} />
                    {isOpen && <span style={{ fontWeight: 400 }}>Panel Rehberi</span>}
                </button>
            )}
            <button className="logout-btn" onClick={() => {
                navigate('/app');
                setTimeout(() => window.dispatchEvent(new Event('show-setup-wizard')), 50);
                if (window.innerWidth <= 1024) setIsOpen(false);
            }} style={{ marginTop: onShowGuide ? '0' : 'auto', color: 'var(--text-muted)', fontWeight: 400 }}>
                <Rocket size={20} />
                {isOpen && <span style={{ fontWeight: 400 }}>Kurulum Sihirbazı</span>}
            </button>
            <button className="logout-btn" onClick={handleLogout}>
                <LogOut size={20} />
                {isOpen && <span>{t('nav.logout')}</span>}
            </button>
        </aside>
    );
};

export default Sidebar;
